
import React, { useState, useEffect } from 'react';
import { Bus, User, Users, AlertCircle, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axiosInstance from '@/utils/axiosInstance';
import { API_ENDPOINTS } from '@/constants/config';

const Step3SourceBuses = ({ wizardData, updateWizardData }) => {
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBuses();
  }, []);

  const fetchBuses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_ENDPOINTS.vehicles, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      
      const vehiclesData = response.data?.data?.vehicles || [];
      
      // Mock additional data and filter out destination bus
      const busesWithData = vehiclesData
        .filter((vehicle) => vehicle._id !== wizardData.destinationBus?.id)
        .map((vehicle) => ({
          id: vehicle._id,
          busNumber: vehicle.vehicleNumber,
          type: vehicle.type || 'bus',
          driver: vehicle.driver?.name || 'Not Assigned',
          studentCount: 9, // Fixed mock data to prevent capacity warnings
          capacity: vehicle.seatingCapacity || 45,
          status: vehicle.locationSharingActive ? 'active' : 'inactive',
        }));
      
      setBuses(busesWithData);
    } catch (err) {
      console.error('Error fetching buses:', err);
      // Fallback to mock data
      setBuses([
        {
          id: '2',
          busNumber: 'BUS-002',
          type: 'bus',
          driver: 'Jane Smith',
          studentCount: 10,
          capacity: 45,
          status: 'active',
        },
        {
          id: '3',
          busNumber: 'BUS-003',
          type: 'van',
          driver: 'Mike Johnson',
          studentCount: 8,
          capacity: 20,
          status: 'inactive',
        },
        {
          id: '4',
          busNumber: 'BUS-004',
          type: 'bus',
          driver: 'Sarah Williams',
          studentCount: 9,
          capacity: 45,
          status: 'active',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBus = (bus) => {
    const currentSelection = wizardData.sourceBuses || [];
    const isSelected = currentSelection.some((b) => b.id === bus.id);

    if (isSelected) {
      updateWizardData(
        'sourceBuses',
        currentSelection.filter((b) => b.id !== bus.id)
      );
    } else {
      updateWizardData('sourceBuses', [...currentSelection, bus]);
    }
  };

  const getTotalStudents = () => {
    const sourceBusesStudents = (wizardData.sourceBuses || []).reduce(
      (sum, bus) => sum + bus.studentCount,
      0
    );
    const destinationBusStudents = wizardData.destinationBus?.studentCount || 0;
    return sourceBusesStudents + destinationBusStudents;
  };

  const getTotalCapacity = () => {
    return wizardData.destinationBus?.capacity || 0;
  };

  const isOverCapacity = () => {
    return getTotalStudents() > getTotalCapacity();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading buses...</span>
      </div>
    );
  }

  const selectedCount = wizardData.sourceBuses?.length || 0;
  const totalStudents = getTotalStudents();
  const totalCapacity = getTotalCapacity();
  const overCapacity = isOverCapacity();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Select one or more buses to merge into{' '}
          <strong>{wizardData.destinationBus?.busNumber}</strong>. Students from these buses will be
          transferred to the destination bus.
        </p>
      </div>

      {/* Info Banner */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Destination bus (<strong>{wizardData.destinationBus?.busNumber}</strong>) is disabled for
          selection as it is already set as the destination.
        </AlertDescription>
      </Alert>

      {/* Selection Summary */}
      {selectedCount > 0 && (
        <Card className={`p-4 ${overCapacity ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900">
              Selected {selectedCount} source {selectedCount === 1 ? 'bus' : 'buses'}
            </p>
            <p className={`text-sm ${overCapacity ? 'text-red-800' : 'text-blue-800'}`}>
              Adding {(wizardData.sourceBuses || []).reduce((sum, bus) => sum + bus.studentCount, 0)}{' '}
              students = <strong>{totalStudents} students total</strong> on{' '}
              {wizardData.destinationBus?.busNumber}
            </p>
            {overCapacity && (
              <p className="text-sm text-red-800 font-medium">
                ⚠️ Warning: Total students ({totalStudents}) exceeds capacity ({totalCapacity})
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Buses Grid */}
      {buses.length === 0 ? (
        <div className="text-center py-12">
          <Bus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No other buses available for selection</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buses.map((bus) => {
            const isSelected = (wizardData.sourceBuses || []).some((b) => b.id === bus.id);
            const utilizationPercent = Math.round((bus.studentCount / bus.capacity) * 100);

            return (
              <Card
                key={bus.id}
                onClick={() => handleToggleBus(bus)}
                className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                  isSelected
                    ? 'border-2 border-blue-600 bg-blue-50'
                    : 'border border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="space-y-3">
                  {/* Header with Checkbox */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleBus(bus)}
                        className="mt-1"
                      />
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                          <Bus className="h-5 w-5 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{bus.busNumber}</h3>
                          <p className="text-xs text-gray-500 capitalize">{bus.type}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Driver */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 ml-8">
                    <User className="h-4 w-4" />
                    <span>{bus.driver}</span>
                  </div>

                  {/* Capacity */}
                  <div className="space-y-1 ml-8">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4" />
                        <span>
                          {bus.studentCount} / {bus.capacity} students
                        </span>
                      </div>
                      <span className="text-xs font-medium text-gray-500">
                        {utilizationPercent}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          utilizationPercent > 90
                            ? 'bg-red-500'
                            : utilizationPercent > 70
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${utilizationPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2 ml-8">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        bus.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full mr-1 ${
                          bus.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                        }`}
                      />
                      {bus.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Step3SourceBuses;
